import React, { useState } from 'react';
import {
  Briefcase,
  TrendingUp,
  Users,
  Clock,
  CreditCard,
  ShieldCheck,
  FileText,
  Globe,
  CheckCircle2,
  Search,
  X,
  ArrowRight
} from 'lucide-react';
import { ERP_MODULES } from '../data/demoContent';

const CATEGORIES = [
  { id: 'all', label: 'All Modules (8)' },
  { id: 'talent', label: 'Talent & Recruitment' },
  { id: 'staffing', label: 'Bench Sales & Staffing' },
  { id: 'operations', label: 'Operations & Workforce' },
  { id: 'finance', label: 'Billing & Invoicing' },
  { id: 'legal', label: 'Legal & Compliance' }
];

const moduleCategoryMap = {
  ats: 'talent',
  bench: 'staffing',
  crm: 'operations',
  timesheets: 'operations',
  invoicing: 'finance',
  h1b: 'legal',
  msa: 'legal',
  portal: 'staffing'
};

const moduleTagStyle = {
  ats: { className: 'jp-tag-fulltime', label: 'RECRUITMENT' },
  bench: { className: 'jp-tag-contract', label: 'BENCH SALES' },
  crm: { className: 'jp-tag-purple', label: 'CLIENT CRM' },
  timesheets: { className: 'jp-tag-parttime', label: 'WORKFORCE' },
  invoicing: { className: 'jp-tag-green', label: 'FINANCE' },
  h1b: { className: 'jp-tag-fulltime', label: 'USCIS LEGAL' },
  msa: { className: 'jp-tag-contract', label: 'CONTRACTS' },
  portal: { className: 'jp-tag-purple', label: 'EXTERNAL' }
};

const MODULE_ICONS = {
  ats: { icon: Briefcase, color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  bench: { icon: TrendingUp, color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc' },
  crm: { icon: Users, color: '#9333ea', bg: '#faf5ff', border: '#e9d5ff' },
  timesheets: { icon: Clock, color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  invoicing: { icon: CreditCard, color: '#059669', bg: '#ecfdf5', border: '#a7f3d0' },
  h1b: { icon: ShieldCheck, color: '#4f46e5', bg: '#eef2ff', border: '#c7d2fe' },
  msa: { icon: FileText, color: '#0284c7', bg: '#f0f9ff', border: '#bae6fd' },
  portal: { icon: Globe, color: '#0d9488', bg: '#f0fdfa', border: '#99f6e4' }
};

const ModuleExplorer = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDrawerModule, setActiveDrawerModule] = useState(null);

  const filteredModules = ERP_MODULES.filter((mod) => {
    const matchesCategory =
      selectedCategory === 'all' || moduleCategoryMap[mod.id] === selectedCategory;
    const matchesSearch =
      mod.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      mod.tagline.toLowerCase().includes(searchQuery.toLowerCase()) ||
      mod.simpleExplanation.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const openDrawer = (mod) => {
    setActiveDrawerModule(mod);
    document.body.style.overflow = 'hidden';
  };

  const closeDrawer = () => {
    setActiveDrawerModule(null);
    document.body.style.overflow = 'auto';
  };

  const scrollToSandbox = () => {
    closeDrawer();
    const el = document.getElementById('sandbox');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const renderDrawerPreviewWidget = (mod) => {
    if (!mod) return null;
    switch (mod.id) {
      case 'ats':
        return (
          <div style={{ background: '#f8fafc', border: '1.5px solid #cbd5e1', borderRadius: '10px', padding: '16px', marginTop: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px', fontWeight: '700' }}>
              <span>Sample Role: Senior Cloud Architect</span>
              <span style={{ color: '#2563eb' }}>12 In Pipeline</span>
            </div>
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong style={{ fontSize: '13px', color: '#0f172a' }}>Alex Rivera</strong>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Stage: Interviewing • 96% Match</div>
              </div>
              <span style={{ fontSize: '11px', fontWeight: '700', color: '#059669', background: '#ecfdf5', padding: '2px 8px', borderRadius: '4px' }}>
                Shortlisted
              </span>
            </div>
          </div>
        );

      case 'timesheets':
        return (
          <div style={{ background: '#f8fafc', border: '1.5px solid #cbd5e1', borderRadius: '10px', padding: '16px', marginTop: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px', fontWeight: '700' }}>
              <span>Timesheet Approvals Queue</span>
              <span style={{ color: '#059669' }}>40.0 Hrs Total</span>
            </div>
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong style={{ fontSize: '13px', color: '#0f172a' }}>Sarah Jenkins (React Dev)</strong>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Client: Citigroup • Bill Rate: $95/hr</div>
              </div>
              <button
                type="button"
                style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
                onClick={scrollToSandbox}
              >
                Simulate Approval
              </button>
            </div>
          </div>
        );

      case 'invoicing':
        return (
          <div style={{ background: '#f8fafc', border: '1.5px solid #cbd5e1', borderRadius: '10px', padding: '16px', marginTop: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px', fontWeight: '700' }}>
              <span>Auto-Generated Invoice</span>
              <span style={{ color: '#2563eb' }}>#INV-2026-104</span>
            </div>
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', fontSize: '12px', color: '#334155' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span>40.0 hrs @ $95/hr:</span>
                <strong>$3,800.00</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '6px', borderTop: '1px solid #e2e8f0', color: '#0f172a', fontWeight: '800' }}>
                <span>Total Due:</span>
                <span style={{ color: '#2563eb' }}>$3,800.00</span>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div style={{ background: '#f8fafc', border: '1.5px solid #cbd5e1', borderRadius: '10px', padding: '16px', marginTop: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '12.5px', color: '#64748b' }}>
              Live interactive simulation ready for this module in the sandbox below.
            </div>
          </div>
        );
    }
  };

  return (
    <section className="jp-workspace-section" id="modules-workspace">
      <div className="jp-workspace-container">
        {/* Section Header */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span style={{ fontSize: '13px', fontWeight: '700', color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              ENTERPRISE MODULE DIRECTORY
            </span>
            <span style={{ fontSize: '12px', color: '#94a3b8' }}>•</span>
            <span style={{ fontSize: '12px', color: '#64748b' }}>Click any card to inspect capabilities</span>
          </div>
          <h2 style={{ fontSize: '26px', fontWeight: '850', color: '#0f172a', margin: '0 0 6px 0', letterSpacing: '-0.5px' }}>
            All 8 Core ERP Modules
          </h2>
          <p style={{ fontSize: '14.5px', color: '#475569', margin: 0 }}>
            Crafted for rapid operational workflows. Choose a category below or explore each module.
          </p>
        </div>

        {/* Filter Toolbar (Segmented Pills & Search Bar) */}
        <div className="jp-filter-toolbar">
          <div className="jp-category-pills">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                type="button"
                className={`jp-category-pill-btn ${selectedCategory === cat.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(cat.id)}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="jp-search-input-box">
            <Search size={15} color="#64748b" />
            <input
              type="text"
              placeholder="Search features or modules..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="jp-search-input"
            />
            {searchQuery && (
              <X size={14} color="#94a3b8" style={{ cursor: 'pointer' }} onClick={() => setSearchQuery('')} />
            )}
          </div>
        </div>

        {/* EXACT CAREER PORTAL 3-COLUMN CARD GRID WITH ICON BUBBLES (Removed active status and prophecy cloud) */}
        <div className="jp-card-grid">
          {filteredModules.map((mod) => {
            const tagInfo = moduleTagStyle[mod.id] || { className: 'jp-tag-fulltime', label: 'MODULE' };
            const iconMeta = MODULE_ICONS[mod.id] || { icon: Briefcase, color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' };
            const IconComp = iconMeta.icon;

            return (
              <div
                key={mod.id}
                className="jp-job-card"
                onClick={() => openDrawer(mod)}
              >
                <div>
                  <div className="jp-card-top-row">
                    <span className={`jp-card-type-tag ${tagInfo.className}`}>
                      {tagInfo.label}
                    </span>

                    {/* Instead of active status text, distinctive colored module icon bubble */}
                    <div
                      className="jp-card-icon-bubble"
                      style={{
                        background: iconMeta.bg,
                        border: `1px solid ${iconMeta.border}`,
                        color: iconMeta.color
                      }}
                      title={mod.title}
                    >
                      <IconComp size={16} />
                    </div>
                  </div>

                  <h3 className="jp-card-title">{mod.title}</h3>
                  <p className="jp-card-desc-text">{mod.tagline}</p>

                  <div className="jp-card-chips-row">
                    {mod.features.slice(0, 3).map((feat, idx) => (
                      <span key={idx} className="jp-card-chip">
                        {feat}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Clean Bottom Row: Removed "Prophecy Cloud" text & dot per user request */}
                <div className="jp-card-bottom-row" style={{ justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="jp-card-apply-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      openDrawer(mod);
                    }}
                  >
                    <span>Explore Module</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {filteredModules.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 0', background: '#ffffff', borderRadius: '12px', border: '1.5px solid #cbd5e1' }}>
            <p style={{ fontSize: '15px', color: '#64748b' }}>No modules matched your search.</p>
            <button
              type="button"
              className="jp-category-pill-btn active"
              onClick={() => { setSelectedCategory('all'); setSearchQuery(''); }}
            >
              Reset Filters
            </button>
          </div>
        )}
      </div>

      {/* SLIDE-OVER MODULE DETAIL DRAWER */}
      {activeDrawerModule && (
        <div className="jp-drawer-backdrop" onClick={closeDrawer}>
          <div className="jp-drawer-container" onClick={(e) => e.stopPropagation()}>
            <div className="jp-drawer-header">
              <div>
                <span className={`jp-card-type-tag ${moduleTagStyle[activeDrawerModule.id]?.className || 'jp-tag-fulltime'}`}>
                  {moduleTagStyle[activeDrawerModule.id]?.label || 'CORE MODULE'}
                </span>
                <h2 style={{ fontSize: '22px', fontWeight: '850', color: '#0f172a', margin: '8px 0 4px 0' }}>
                  {activeDrawerModule.title}
                </h2>
                <div style={{ fontSize: '13px', color: '#2563eb', fontWeight: '600' }}>
                  {activeDrawerModule.tagline}
                </div>
              </div>

              <button type="button" className="jp-drawer-close-btn" onClick={closeDrawer} aria-label="Close">
                <X size={18} />
              </button>
            </div>

            <div className="jp-drawer-content">
              <div style={{ marginBottom: '22px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px 0' }}>
                  What Problem Does It Solve?
                </h4>
                <p style={{ fontSize: '14px', color: '#334155', lineHeight: '1.6', margin: 0 }}>
                  {activeDrawerModule.simpleExplanation}
                </p>
              </div>

              <div style={{ marginBottom: '22px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 10px 0' }}>
                  Key Capabilities:
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {activeDrawerModule.features.map((feat, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13.5px', color: '#334155' }}>
                      <CheckCircle2 size={16} color="#059669" style={{ flexShrink: 0 }} />
                      <span>{feat}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px 0' }}>
                  Live Interactive Preview:
                </h4>
                {renderDrawerPreviewWidget(activeDrawerModule)}
              </div>

              <button
                type="button"
                className="erp-demo-btn-primary"
                onClick={scrollToSandbox}
                style={{ width: '100%', justifyContent: 'center', padding: '11px 0', fontSize: '14px' }}
              >
                <span>Test-Drive in Live Sandbox Below</span>
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ModuleExplorer;
